import { atomWithStorage } from "jotai/utils";
import { useAtom } from "jotai";
import { useEffect } from "react";
import {
  DEFAULT_THEME,
  resolveThemeId,
  THEME_STORAGE_KEY,
  type ThemeId,
} from "@/lib/themes";

/*
  Stored as a bare string, not JSON. `atomWithStorage`'s default storage would write
  `"sheet"` with the quotes, and the boot script in index.html — which runs before
  any bundle and cannot import this module — would have to strip them. One unquoted
  value read by both is the smaller surface.

  A retired id maps to its successor (LEGACY_THEMES, ADR 0011) — the boot script has
  normally rewritten it already, this covers the case where that write failed. Any
  other unrecognised value falls back to the default rather than reaching the DOM,
  where it would match no block and paint Sheet's `:root` under another name.
*/
const themeStorage = {
  getItem: (key: string, initialValue: ThemeId): ThemeId =>
    resolveThemeId(localStorage.getItem(key)) ?? initialValue,
  setItem: (key: string, value: ThemeId) => localStorage.setItem(key, value),
  removeItem: (key: string) => localStorage.removeItem(key),
};

export const themeAtom = atomWithStorage<ThemeId>(
  THEME_STORAGE_KEY,
  DEFAULT_THEME,
  themeStorage,
  { getOnInit: true }
);

/**
 * Reads and sets the active theme.
 *
 * Only the value is state — applying it is `useApplyTheme`, mounted once at the root,
 * so a component that merely wants to know the theme (the strands canvas) does not
 * also have to own writing it to the DOM.
 */
export function useTheme() {
  return useAtom(themeAtom);
}

/**
 * Mirrors the stored theme onto `<html data-theme>`. Mount once, at the root.
 *
 * The attribute is already correct on first paint — index.html sets it from the same
 * key before the bundle loads — so this is what keeps it correct after a change, and
 * the resync if another tab wrote a different theme.
 */
export function useApplyTheme() {
  const [theme] = useTheme();

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);
}
